import { Controller } from "@hotwired/stimulus"

// Toggles the web widget preview iframe between a desktop (full width) and a mobile (~390px phone
// frame) render. Narrowing the frame is what drives the layout — the widget reads the iframe's own
// window.innerWidth — but width alone is ambiguous: the settings pane can be just as narrow while
// still wanting the desktop look, so the widget pins desktop by default in preview. We therefore also
// state which device we meant (shipped:preview-device -> setPreviewDevice). Without that message
// "Mobile" renders a slightly-smaller desktop window instead of a phone.
const MOBILE_WIDTH = "390px"

export default class extends Controller {
  static targets = ["frame", "button"]
  static values = { device: { type: String, default: "desktop" } }

  connect() {
    this.apply()

    // Same morph problem as the theme-preview controller next door: the inline width and the
    // active-segment classes are ours, not the server's, so a morph strips them and the surviving
    // element means connect() never re-runs to put them back.
    this.restoreAfterMorph = this.restoreAfterMorph.bind(this)
    document.addEventListener("turbo:morph", this.restoreAfterMorph)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.restoreAfterMorph)
  }

  restoreAfterMorph() {
    // deviceValue is a Stimulus value, i.e. a data attribute — and the server doesn't render it, so the
    // morph strips it and we'd silently snap back to Desktop. `chosen` is a plain property, so it
    // survives; re-assert it (which re-runs apply() via deviceValueChanged). If it didn't change, just
    // re-apply the width and segment classes the morph took.
    if (this.chosen && this.deviceValue !== this.chosen) {
      this.deviceValue = this.chosen
    } else {
      this.apply()
    }
  }

  select(event) {
    event.preventDefault()
    this.chosen = event.currentTarget.dataset.device
    this.deviceValue = this.chosen
  }

  deviceValueChanged() {
    this.apply()
  }

  // Also wired to the iframe's `load`: a fresh document starts at the widget's own default (desktop),
  // so the choice has to be replayed into it — the same reason the theme controller replays its state.
  apply() {
    const mobile = this.deviceValue === "mobile"
    if (this.hasFrameTarget) {
      this.frameTarget.style.width = mobile ? MOBILE_WIDTH : ""
    }
    this.buttonTargets.forEach((button) => {
      const active = button.dataset.device === this.deviceValue
      button.classList.toggle("bg-white", active)
      button.classList.toggle("shadow-sm", active)
    })
    this.post()
  }

  post() {
    if (!this.hasFrameTarget || !this.frameTarget.contentWindow) return

    // Same-origin target: the preview iframe is our own preview action (see the widget-theme-preview
    // controller); scope the message to this origin rather than broadcasting to any frame.
    this.frameTarget.contentWindow.postMessage(
      { type: "shipped:preview-device", device: this.deviceValue },
      window.location.origin
    )
  }
}
